import React, { useState, useEffect, useRef } from 'react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, IconButton, Menu, MenuItem, 
  TextField, FormControl, InputLabel, Select, Dialog, DialogTitle, 
  DialogContent, DialogActions, Button, Snackbar, Alert, CircularProgress,
  Tooltip, Chip, TablePagination,
  Popover, Divider
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Pending,
  TrendingUp,
  Assignment,
  Email,
  CloudUpload,
  Refresh as RefreshIcon,
  ContentCopy,
  History,
  StickyNote2 as StickyNote2Icon,
  NoteAdd as NoteAddIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import BranchManagementModal from '../servicios/BranchManagementModal';
import QuotationPreview from './QuotationPreview';
import CostReviewDialog from './CostReviewDialog';
import { Visibility as VisibilityIcon, ReceiptLong as ReceiptLongIcon } from '@mui/icons-material';
import { calculateIva } from '../../utils/quotationTax';


const getUserRole = () => {
  try {
    const role = JSON.parse(sessionStorage.getItem('userInfo') || '{}')?.role;
    return ((typeof role === 'string' ? role : role?.name) || '').toUpperCase();
  } catch {
    return '';
  }
};

const EstadisticasCotizaciones = () => {
  const navigate = useNavigate();
  
  // Snackbar visual
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Manejar cambio de estatus de cotización
  // Solo puede salir de Borrador hacia Pendiente mandando el correo de verdad (botón "Enviar" ->
  // markAsSent() en backend); Aceptada/Completada/Facturada/Pagada tampoco tienen sentido sin
  // haberse enviado antes. Rechazada/Cancelada/Expirada sí se permiten directo -- abandonar una
  // cotización que nunca se mandó no debería obligar a "enviarla" primero. El backend valida lo
  // mismo (PUT /{id}/status) por si este check del frontend se llega a saltar.
  const BLOCKED_FROM_DRAFT = new Set(['PENDING', 'ACCEPTED', 'COMPLETED', 'INVOICED', 'PAID']);
  const QUOTATION_STATUS_OPTIONS = [
    ['DRAFT', 'Borrador'],
    ['PENDING', 'Pendiente'],
    ['ACCEPTED', 'Aceptada'],
    ['REJECTED', 'Rechazada'],
    ['EXPIRED', 'Expirada'],
    ['CANCELLED', 'Cancelada'],
    ['COMPLETED', 'Completada'],
    ['INVOICED', 'Facturada'],
    ['PAID', 'Pagada'],
  ];
  // Reglas en el backend (QuotationLifecycleService): aquí solo se ofrecen los estados permitidos.
  const canChangeStatus = ['ADMINISTRADOR', 'VENTAS'].includes(getUserRole());
  const allowedTargets = (q) => (canChangeStatus ? (q?.allowedStatusTransitions || []) : []);

  // Mismo criterio que QuotationService.isEditable(): el cliente aún no la tiene. Una vez enviada
  // no puede regresar a Borrador (reabriría la edición); se reemplaza con una nueva versión.
// Texto del resultado del aviso de cancelación al cliente (respuesta del backend: clientNotice)
const cancellationNoticeTextFor = (data) => {
  if (!data || !data.clientNotice) return null;
  if (data.clientNotice === 'SENT') return { severity: 'success', text: `Se avisó al cliente de la cancelación (${data.recipient}).` };
  if (data.clientNotice === 'SENDING') return { severity: 'info', text: `El aviso de cancelación va en camino a ${data.recipient}; llegará en unos minutos.` };
  if (data.clientNotice === 'NOT_SENT') return { severity: 'info', text: 'No se avisó al cliente: la cotización nunca se le envió.' };
  return { severity: 'warning', text: data.clientNoticeMessage || 'No se pudo avisar al cliente de la cancelación.' };
};

  const canReturnToDraft = (q) =>!q?.status || q.status === 'DRAFT' || (q.status === 'PENDING' && !q.sentAt);

  const handleStatusChange = async (quotation, newStatus) => {
    if (newStatus === 'DRAFT' && !canReturnToDraft(quotation)) {
      setSnackbar({ open: true, message: 'Esta cotización ya se envió al cliente y no puede regresar a Borrador. Si necesitas cambiarla, crea una nueva versión desde la cotización.', severity: 'warning' });
      return;
    }
    if (quotation.status === 'DRAFT' && BLOCKED_FROM_DRAFT.has(newStatus)) {
      setSnackbar({
        open: true,
        message: 'Esta cotización sigue en Borrador. Usa el botón "Enviar" para mandarla al cliente antes de cambiar su estado.',
        severity: 'warning'
      });
      return;
    }
    setLoading(true);
    try {
      // 1. Actualizar estatus de cotización (el backend registra el evento automáticamente)
      const statusResponse = await api.put(`/quotations/${quotation.id}/status`, { status: newStatus });
      
      // 2. Si el estado es ACCEPTED, obtener fecha de aceptación y orden de trabajo
      let workOrderInfo = null;
      let acceptedAt = null;
      
      if (newStatus === 'ACCEPTED') {
        // Obtener la fecha de aceptación del backend o usar la actual
        acceptedAt = statusResponse.data.acceptedAt || new Date().toISOString();
        
        // Verificar si se generó una orden de trabajo
        if (statusResponse.data.workOrderNumber) {
          workOrderInfo = {
            orderNumber: statusResponse.data.workOrderNumber,
            message: 'Orden de trabajo generada automáticamente'
          };
        }
      }
      
      // 3. Mostrar mensaje de éxito
      if (workOrderInfo) {
        setSnackbar({ 
          open: true, 
          message: `Estatus actualizado a ${newStatus}. Orden de trabajo generada: ${workOrderInfo.orderNumber}`, 
          severity: 'success' 
        });
      } else if (newStatus === 'CANCELLED') {
        const notice = cancellationNoticeTextFor(statusResponse.data);
        setSnackbar({
          open: true,
          message: `Cotización cancelada.${notice ? ' ' + notice.text : ''}${notice && notice.severity === 'warning' ? ' Puedes reintentarlo desde la cotización.' : ''}`,
          severity: notice && notice.severity === 'warning' ? 'warning' : 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: `Estatus actualizado a ${newStatus} correctamente.`,
          severity: 'success'
        });
      }
      
      // 4. Actualizar estado local con fecha de aceptación y orden de trabajo
      const updatedQuotation = {
        ...quotation,
        status: newStatus,
        ...(newStatus === 'ACCEPTED' && {
          acceptedAt: acceptedAt,
          accepted_at: acceptedAt,
          workOrderNumber: workOrderInfo?.orderNumber || quotation.workOrderNumber
        })
      };
      
      setQuotations(prev => prev.map(q => q.id === quotation.id ? updatedQuotation : q));
      setFilteredQuotations(prev => prev.map(q => q.id === quotation.id ? updatedQuotation : q));
      
      // 5. Recalcular estadísticas
      const updatedQuotations = quotations.map(q => q.id === quotation.id ? updatedQuotation : q);
      calculateStats(updatedQuotations);
      
    } catch (err) {
      console.error('Error al actualizar el estatus:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Error al actualizar el estatus. Intente de nuevo.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };


  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Paginación server-side
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    expired: 0,
    inProgress: 0
  });
  // Estados para el menú de acciones
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [actionMenuQuotation, setActionMenuQuotation] = useState(null);
  const [invoiceQuotation, setInvoiceQuotation] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  // Ventas/Administración resuelven las revisiones de costo por correcciones en sitio
  const [canResolveCostReviews, setCanResolveCostReviews] = useState(false);
  const [costReviewQuotation, setCostReviewQuotation] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const [deletingQuotation, setDeletingQuotation] = useState(false);
  const [filters, setFilters] = useState({
    folio: '',
    cliente: '',
    empresa: '',
    status: '',
    email: '',
    creationMonth: ''
  });
  const location = useLocation();
  // Desde Home ("Ver borradores") se llega con la card de Borrador ya seleccionada
  const [selectedCardFilter, setSelectedCardFilter] = useState(location.state?.cardFilter || '');
  
  // Estados para edición de cotización
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Estados para popover de notas
  const [notesAnchorEl, setNotesAnchorEl] = useState(null);
  const [activeNotesQuotationId, setActiveNotesQuotationId] = useState(null);
  const [notesList, setNotesList] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesCount, setNotesCount] = useState({});
  const suppressRowClickRef = useRef(false);

  // Estados para sistema de versiones
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  
  // Estados para gestión de sucursales
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [selectedQuotationForBranches, setSelectedQuotationForBranches] = useState(null);
  const [selectedQuotationForVersion, setSelectedQuotationForVersion] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Estado para preview de cotización
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewQuotationId, setPreviewQuotationId] = useState(null);

  // Funciones de notas
  const fetchNotes = async (quotationId) => {
    setNotesLoading(true);
    try {
      const res = await api.get(`/quotations/${quotationId}/notes`);
      const data = res.data || [];
      setNotesList(data);
      setNotesCount(prev => ({ ...prev, [quotationId]: data.length }));
    } catch (e) {
      setNotesList([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleOpenNotes = async (event, quotation) => {
    setNotesAnchorEl(event.currentTarget);
    setActiveNotesQuotationId(quotation.id);
    setNoteInput('');
    await fetchNotes(quotation.id);
  };

  const handleCloseNotes = () => {
    suppressRowClickRef.current = true;
    setTimeout(() => { suppressRowClickRef.current = false; }, 100);
    setNotesAnchorEl(null);
    setActiveNotesQuotationId(null);
    setNotesList([]);
    setNoteInput('');
  };

  const handleSaveNote = async () => {
    if (!noteInput.trim() || !activeNotesQuotationId) return;
    setNotesSaving(true);
    try {
      await api.post(`/quotations/${activeNotesQuotationId}/notes`, { content: noteInput.trim() });
      setNoteInput('');
      await fetchNotes(activeNotesQuotationId);
      setSnackbar({ open: true, message: 'Nota agregada exitosamente', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al guardar la nota', severity: 'error' });
    } finally {
      setNotesSaving(false);
    }
  };

  // Función para manejar clic en fila de cotización - Redirige a edición
  const handleRowClick = (quotation, event) => {
    // Bloquear si hay un popover/menú abierto o acaba de cerrarse
    if (suppressRowClickRef.current || notesAnchorEl || actionMenuAnchorEl) return;

    // Evitar redirigir si se hizo click en el Select de estatus o botones de acciones
    if (event && (
      event.target.closest('.MuiSelect-root') || 
      event.target.closest('.MuiMenuItem-root') ||
      event.target.closest('.MuiIconButton-root') ||
      event.target.closest('button')
    )) {
      return;
    }
    
    // Redirigir a la pantalla de edición con el ID de la cotización
    navigate(`/cotizaciones/new-quotation?edit=${quotation.id}`);
  };

  // Función para guardar cambios
  const handleSaveQuotation = async () => {
    // Mismo bloqueo que handleStatusChange -- el diálogo de edición es otra vía para cambiar
    // el status, y sin este chequeo también podía "enviar por escritorio" una cotización que
    // nunca se mandó de verdad.
    if (editFormData.status === 'DRAFT' && editingQuotation?.status !== 'DRAFT' && !canReturnToDraft(editingQuotation)) {
      setSnackbar({ open: true, message: 'Esta cotización ya se envió al cliente y no puede regresar a Borrador. Si necesitas cambiarla, crea una nueva versión desde la cotización.', severity: 'warning' });
      return;
    }
    if (editingQuotation?.status === 'DRAFT' && BLOCKED_FROM_DRAFT.has(editFormData.status)) {
      setSnackbar({
        open: true,
        message: 'Esta cotización sigue en Borrador. Usa el botón "Enviar" para mandarla al cliente antes de cambiar su estado.',
        severity: 'warning'
      });
      return;
    }
    try {
      setLoading(true);

      // Enviar solo los campos editables
      const dataToSend = {
        status: editFormData.status,
        requiresInvoice: editFormData.requiresInvoice,
        comments: editFormData.comments
      };
      
      const updateResponse = await api.put(`/quotations/${editingQuotation.id}/update-fields`, dataToSend);
      const cancelNotice = cancellationNoticeTextFor(updateResponse.data);
      if (cancelNotice && cancelNotice.severity !== 'success') {
        setTimeout(() => setSnackbar({ open: true, message: `${cancelNotice.text}${cancelNotice.severity === 'warning' ? ' Puedes reintentarlo desde la cotización.' : ''}`, severity: cancelNotice.severity }), 2500);
      }
      
      setSnackbar({
        open: true,
        message: 'Cotización actualizada exitosamente',
        severity: 'success'
      });
      
      setEditDialogOpen(false);
      setEditingQuotation(null);
      fetchQuotations(); // Recargar datos
    } catch (error) {
      console.error('Error al guardar cotización:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error al guardar los cambios',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar cambios en el formulario
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para manejar cambios en días laborales
  const handleWorkDayChange = (day) => {
    setEditFormData(prev => ({
      ...prev,
      workDays: {
        ...prev.workDays,
        [day]: !prev.workDays[day]
      }
    }));
  };

  // ========== FUNCIONES PARA SISTEMA DE VERSIONES ==========
  
  // Abrir dialog de confirmación para crear nueva versión
  const handleOpenVersionDialog = (quotation) => {
    setSelectedQuotationForVersion(quotation);
    setVersionDialogOpen(true);
  };

  // Crear nueva versión de cotización y redirigir a edición
  const handleCreateNewVersion = async () => {
    if (!selectedQuotationForVersion) return;
    
    setLoading(true);
    try {
      const response = await api.post(`/quotations/${selectedQuotationForVersion.id}/create-version`);
      
      const newQuotationId = response.data.quotation?.id;
      const newFolio = response.data.newFolio;
      
      setVersionDialogOpen(false);
      setSelectedQuotationForVersion(null);
      
      // Mostrar mensaje de éxito
      setSnackbar({
        open: true,
        message: `Nueva versión creada: ${newFolio}. Redirigiendo a edición...`,
        severity: 'success'
      });
      
      // Redirigir a la pantalla de nueva cotización con el ID para edición
      setTimeout(() => {
        navigate(`/cotizaciones/new-quotation?edit=${newQuotationId}`);
      }, 1000);
      
    } catch (error) {
      console.error('Error al crear nueva versión:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error al crear nueva versión',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  // Obtener y mostrar historial de versiones
  const handleShowVersionHistory = async (quotation) => {
    setLoadingVersions(true);
    setHistoryDialogOpen(true);
    
    try {
      const response = await api.get(`/quotations/${quotation.id}/version-history`);
      setVersionHistory(response.data.versions || []);
    } catch (error) {
      console.error('Error al obtener historial:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar historial de versiones',
        severity: 'error'
      });
      setVersionHistory([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  // Función para convertir días laborales a string
  const workDaysToString = (workDays) => {
    const days = [];
    if (workDays.lunes) days.push('Lunes');
    if (workDays.martes) days.push('Martes');
    if (workDays.miercoles) days.push('Miércoles');
    if (workDays.jueves) days.push('Jueves');
    if (workDays.viernes) days.push('Viernes');
    if (workDays.sabado) days.push('Sábado');
    if (workDays.domingo) days.push('Domingo');
    return days.join(', ');
  };

  useEffect(() => {
    fetchQuotations();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/users/me');
        setIsAdmin(response.data?.role === 'ADMINISTRADOR');
        setCanResolveCostReviews(['ADMINISTRADOR', 'VENTAS'].includes(response.data?.role));
      } catch (err) {
        console.error('Error al obtener usuario actual:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  const handleCloseDeleteConfirm = () => {
    if (deletingQuotation) return;
    setDeleteConfirmOpen(false);
    setQuotationToDelete(null);
  };

  const handleConfirmDeleteQuotation = async () => {
    if (!quotationToDelete) return;
    try {
      setDeletingQuotation(true);
      await api.delete(`/quotations/${quotationToDelete.id}`);
      setDeleteConfirmOpen(false);
      setQuotationToDelete(null);
      fetchQuotations();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error al eliminar la cotización: ' + (err.response?.data?.error || err.message),
        severity: 'error'
      });
    } finally {
      setDeletingQuotation(false);
    }
  };

  // Resetear página cuando cambian filtros
  useEffect(() => {
    setPage(0);
  }, [filters.folio, filters.cliente, filters.empresa, filters.status, filters.email, filters.creationMonth, selectedCardFilter]);

  // Facturar: solo la versión vigente Aceptada/Completada (QuotationLifecycleService.invoiceViolation)
  const canRegisterInvoice = (q) => canChangeStatus && q?.isLatestVersion !== false
    && ['ACCEPTED', 'COMPLETED'].includes(q?.status);

  const openInvoiceDialog = (q) => {
    setInvoiceQuotation(q);
    setInvoiceNumber('');
    setInvoiceError('');
  };

  const handleRegisterInvoice = async () => {
    const number = invoiceNumber.trim();
    if (!number) {
      setInvoiceError('Captura el número de factura');
      return;
    }
    setInvoiceSaving(true);
    try {
      await api.put(`/quotations/${invoiceQuotation.id}/invoice`, { invoiceNumber: number });
      setSnackbar({ open: true, message: `Factura ${number} registrada; la cotización quedó como Facturada`, severity: 'success' });
      setInvoiceQuotation(null);
      fetchQuotations();
    } catch (err) {
      setInvoiceError(err.response?.data?.error || 'No se pudo registrar la factura');
    } finally {
      setInvoiceSaving(false);
    }
  };

  // silent: actualización automática -- sin spinner ni pantalla de error si falla una vuelta
  const fetchQuotations = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('size', String(rowsPerPage));

      // Enviar filtros al backend (server-side)
      if (filters.folio) params.set('folio', filters.folio);
      if (filters.cliente) params.set('cliente', filters.cliente);
      if (filters.empresa) params.set('empresa', filters.empresa);
      if (filters.email) params.set('email', filters.email);
      if (filters.creationMonth) params.set('creationMonth', filters.creationMonth);

      // status: si hay card seleccionada (y no es total), manda ese status; si no, manda el filtro del select
      const effectiveStatus = (selectedCardFilter && selectedCardFilter !== 'total')
        ? selectedCardFilter
        : (filters.status || '');
      if (effectiveStatus) params.set('status', effectiveStatus);

      const response = await api.get(`/quotations/paged?${params.toString()}`);
      const pageData = response.data;
      const quotationsData = pageData?.content || [];

      setTotalElements(pageData?.totalElements ?? 0);

      // Debug: revisar los campos de monto en la primera cotización
      if (quotationsData.length > 0) {
        console.log('Primera cotización - campos de monto:', {
          totalAmount: quotationsData[0].totalAmount,
          total_amount: quotationsData[0].total_amount,
          amount: quotationsData[0].amount,
          subtotal: quotationsData[0].subtotal,
          total: quotationsData[0].total
        });
      }
      setQuotations(quotationsData);
      setFilteredQuotations(quotationsData);

      // Poblar conteo de notas desde el campo noteCount del DTO
      const counts = {};
      quotationsData.forEach(q => {
        if (q.noteCount != null && q.noteCount > 0) counts[q.id] = q.noteCount;
      });
      setNotesCount(prev => ({ ...prev, ...counts }));

      // Nota: stats ahora no puede calcularse exacto solo con una página.
      // Por el momento, mantenemos "total" basado en totalElements y los demás en base a la página actual.
      calculateStats(quotationsData, pageData?.totalElements ?? 0);
    } catch (err) {
      if (silent) return;
      const isNetworkError = !err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
      setError(isNetworkError ? 'network' : 'server');
      console.error('Error fetching quotations:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useAutoRefresh(() => fetchQuotations({ silent: true }), {
    paused: deleteConfirmOpen || editDialogOpen || versionDialogOpen || branchModalOpen || historyDialogOpen
      || previewOpen || !!costReviewQuotation || !!invoiceQuotation,
  });

  const calculateStats = (data, totalFromServer) => {
    // Monto con IVA, igual que el PDF: totalAmount es el subtotal de los servicios (el IVA va aparte).
    const totalAmount = data.reduce((sum, q) => sum + calculateIva(q.totalAmount || 0).totalWithIva, 0);
    
    const stats = {
      total: totalFromServer,
      draft: data.filter(q => q.status === 'DRAFT').length,
      pending: data.filter(q => q.status === 'PENDING').length,
      accepted: data.filter(q => q.status === 'ACCEPTED').length,
      rejected: data.filter(q => q.status === 'REJECTED').length,
      expired: data.filter(q => q.status === 'EXPIRED').length,
      cancelled: data.filter(q => q.status === 'CANCELLED').length,
      totalAmount: totalAmount
    };
    setStats(stats);
  };

  // Filtrar cotizaciones
  useEffect(() => {
    // Con paginación server-side, ya estamos filtrando en backend.
    // Aquí solo aplicamos filtros que todavía sean UI-only (si se agregan).
    let filtered = quotations;

    if (filters.folio) {
      filtered = filtered.filter(q => 
        (q.invoiceFolio || `COT-${String(q.id).padStart(6, '0')}`)
          .toLowerCase()
          .includes(filters.folio.toLowerCase())
      );
    }

    if (filters.cliente) {
      filtered = filtered.filter(q => 
        q.name?.toLowerCase().includes(filters.cliente.toLowerCase())
      );
    }

    if (filters.empresa) {
      filtered = filtered.filter(q => 
        q.businessName?.toLowerCase().includes(filters.empresa.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter(q => q.status === filters.status);
    }

    if (filters.email) {
      filtered = filtered.filter(q => 
        q.email?.toLowerCase().includes(filters.email.toLowerCase())
      );
    }

    if (filters.creationMonth) {
      filtered = filtered.filter(q => {
        const dateField = q.createdAt || q.created_at || q.dateCreated || q.creationDate;
        if (!dateField) return false;
        const createdDate = new Date(dateField);
        if (isNaN(createdDate.getTime())) return false;
        const monthYear = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;
        return monthYear === filters.creationMonth;
      });
    }

    // Filtro por card seleccionada
    if (selectedCardFilter && selectedCardFilter !== 'total') {
      filtered = filtered.filter(q => q.status === selectedCardFilter);
    }

    setFilteredQuotations(filtered);
  }, [filters.folio, filters.cliente, filters.empresa, filters.status, filters.email, filters.creationMonth, quotations, selectedCardFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCardClick = (cardType) => {
    if (selectedCardFilter === cardType) {
      setSelectedCardFilter(''); // Deseleccionar si ya está seleccionada
    } else {
      setSelectedCardFilter(cardType);
    }
    // Limpiar filtro de estado de columna cuando se usa filtro de card
    if (cardType !== 'total') {
      setFilters(prev => ({ ...prev, status: '' }));
    }
  };

  const getAvailableMonths = () => {
    const months = new Set();
    quotations.forEach(q => {
      // Verificar diferentes posibles nombres de campo de fecha
      const dateField = q.createdAt || q.created_at || q.dateCreated || q.creationDate;
      if (dateField) {
        const date = new Date(dateField);
        if (!isNaN(date.getTime())) { // Verificar que la fecha sea válida
          const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          months.add(monthYear);
        }
      }
    });
    return Array.from(months).sort().reverse();
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      // Antes no existía esta entrada -- al caer al fallback de PENDING, una cotización
      // guardada pero NUNCA enviada se veía con el mismo chip "Pendiente" (que en realidad
      // significa "ya se envió, esperando respuesta del cliente"). Quedaba indistinguible de
      // una que sí se mandó.
      DRAFT: { label: 'Borrador (no enviada)', color: 'default', icon: <EditIcon /> },
      PENDING: { label: 'Pendiente', color: 'warning', icon: <Pending /> },
      ACCEPTED: { label: 'Aceptada', color: 'success', icon: <CheckCircle /> },
      REJECTED: { label: 'Rechazada', color: 'error', icon: <Cancel /> },
      EXPIRED: { label: 'Expirada', color: 'default', icon: <Schedule /> },
      CANCELLED: { label: 'Cancelada', color: 'secondary', icon: <Cancel /> },
      COMPLETED: { label: 'Completada', color: 'primary', icon: <CheckCircle /> },
      INVOICED: { label: 'Facturada', color: 'info', icon: <Assignment /> },
      PAID: { label: 'Pagada', color: 'success', icon: <CheckCircle /> }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Verificar diferentes posibles nombres de campo de fecha
    const dateField = dateString;
    if (!dateField) return 'N/A';
    
    const date = new Date(dateField);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Enviar correo directamente desde acción
  const handleSendReminderDirect = async (quotation) => {
    try {
      setLoading(true);
      const response = await api.post(`/quotations/${quotation.id}/send-reminder`, {
        clientEmail: quotation.email,
        quotationFolio: quotation.folio,
        clientName: quotation.cliente,
        companyName: quotation.empresa
      });
      if (response.status === 200) {
        setSnackbar({ open: true, message: `Correo reenviado exitosamente a ${quotation.email}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'No se pudo reenviar el correo. Intente de nuevo.', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al reenviar el correo. Intente de nuevo.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Guardar cotización en Dropbox
  const handleSaveToDropbox = async (quotation) => {
    try {
      setLoading(true);
      const response = await api.post(`/quotations/${quotation.id}/save-to-dropbox`);
      
      if (response.data.success === 'true') {
        setSnackbar({ 
          open: true, 
          message: `Cotización guardada en Dropbox como ${response.data.fileName}`, 
          severity: 'success' 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: response.data.message || 'Error al guardar en Dropbox', 
          severity: 'error' 
        });
      }
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: 'Error al guardar cotización en Dropbox. Intente de nuevo.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && typeof error === 'string') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          gap: 3,
          p: 4,
        }}
      >
        {/* Ilustración SVG: cable desconectado */}
        <svg width="220" height="180" viewBox="0 0 220 180" xmlns="http://www.w3.org/2000/svg">
          {/* Contacto / pared */}
          <rect x="10" y="40" width="78" height="100" rx="10" fill="#e3f2fd" stroke="#90caf9" strokeWidth="2"/>
          <rect x="30" y="62" width="14" height="22" rx="4" fill="#1976d2" opacity="0.35"/>
          <rect x="54" y="62" width="14" height="22" rx="4" fill="#1976d2" opacity="0.35"/>
          <rect x="39" y="94" width="20" height="16" rx="10" fill="#1976d2" opacity="0.35"/>
          {/* Cable que cuelga */}
          <path d="M 155 90 C 155 120, 140 140, 115 158" stroke="#ff7043" strokeWidth="7" fill="none" strokeLinecap="round"/>
          {/* Clavija (plug) */}
          <rect x="130" y="68" width="42" height="36" rx="6" fill="#ff7043"/>
          <rect x="140" y="52" width="7" height="20" rx="3" fill="#bf360c"/>
          <rect x="153" y="52" width="7" height="20" rx="3" fill="#bf360c"/>
          {/* Destellos */}
          <text x="97" y="72" fontSize="22" fill="#ffa726">⚡</text>
          {/* Puntos animados de separación */}
          <circle cx="108" cy="90" r="4" fill="#1976d2" opacity="0.5"/>
          <circle cx="120" cy="88" r="3" fill="#1976d2" opacity="0.35"/>
          <circle cx="130" cy="85" r="2" fill="#1976d2" opacity="0.2"/>
        </svg>

        <Typography variant="h5" fontWeight="bold" color="text.primary">
          Sin conexión al servidor
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" maxWidth={380}>
          {error === 'network'
            ? 'No se pudo conectar con el servidor. Verifica que el backend esté activo e intenta de nuevo.'
            : 'El servidor respondió con un error inesperado. Intenta recargar la página.'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => fetchQuotations()}
          sx={{ mt: 1, borderRadius: 2, px: 4 }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        message={snackbar.message}
        ContentProps={{
          sx: {
            bgcolor: snackbar.severity === 'success' ? '#388e3c' : '#d32f2f',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '1rem',
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      />

      {/* Diálogo profesional de confirmación para enviar recordatorio */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Cotizaciones
        </Typography>
        <IconButton 
          onClick={() => window.location.reload()} 
          sx={{ 
            bgcolor: 'primary.main',
            color: 'white',
            boxShadow: 2,
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'rotate(180deg)',
              transition: 'all 0.3s ease-in-out'
            }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Mostrar mensajes de éxito o error */}
      {error && typeof error === 'object' && (
        <Alert 
          severity={error.type || 'error'} 
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error.message}
        </Alert>
      )}

      {/* Cards de Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 12 / 7 }} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              height: '120px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: selectedCardFilter === 'total' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => handleCardClick('total')}
          >
            <CardContent sx={{ 
              textAlign: 'center', 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <TrendingUp sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total
              </Typography>
              <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 'bold', mt: 0.5 }}>
                {formatCurrency(stats.totalAmount || 0)} con IVA
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 12 / 7 }} sx={{ display: 'flex' }}>
          <Card
            sx={{
              bgcolor: '#eeeffb',
              borderLeft: '4px solid #5c6bc0',
              height: '120px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: selectedCardFilter === 'DRAFT' ? '2px solid #5c6bc0' : '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => handleCardClick('DRAFT')}
          >
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#5c6bc0">Borradores</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.draft}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 12 / 7 }} sx={{ display: 'flex' }}>
          <Card
            sx={{
              bgcolor: '#fff3e0',
              borderLeft: '4px solid #ff9800',
              height: '120px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: selectedCardFilter === 'PENDING' ? '2px solid #ff9800' : '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => handleCardClick('PENDING')}
          >
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#ff9800">Pendientes</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 12 / 7 }} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              bgcolor: '#e8f5e8', 
              borderLeft: '4px solid #4caf50',
              height: '120px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: selectedCardFilter === 'ACCEPTED' ? '2px solid #4caf50' : '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => handleCardClick('ACCEPTED')}
          >
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#4caf50">Aceptadas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.accepted}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 12 / 7 }} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              bgcolor: '#ffebee', 
              borderLeft: '4px solid #f44336',
              height: '120px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: selectedCardFilter === 'REJECTED' ? '2px solid #f44336' : '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => handleCardClick('REJECTED')}
          >
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#f44336">Rechazadas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.rejected}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 12 / 7 }} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              bgcolor: '#fafafa', 
              borderLeft: '4px solid #757575',
              height: '120px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: selectedCardFilter === 'EXPIRED' ? '2px solid #757575' : '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => handleCardClick('EXPIRED')}
          >
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#757575">Expiradas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.expired}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 12 / 7 }} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              bgcolor: '#f7f7f7', 
              borderLeft: '4px solid #666',
              height: '120px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: selectedCardFilter === 'CANCELLED' ? '2px solid #666' : '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
            onClick={() => handleCardClick('CANCELLED')}
          >
            <CardContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography variant="h6" color="#666">Canceladas</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stats.cancelled}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de Cotizaciones */}
      <Paper elevation={3}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Listado de Cotizaciones
          </Typography>
          
          {/* Filtros */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por Folio"
                value={filters.folio}
                onChange={(e) => handleFilterChange('folio', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por Cliente"
                value={filters.cliente}
                onChange={(e) => handleFilterChange('cliente', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por Empresa"
                value={filters.empresa}
                onChange={(e) => handleFilterChange('empresa', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.status}
                  label="Estado"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="DRAFT">Borrador</MenuItem>
                  <MenuItem value="PENDING">Pendiente</MenuItem>
                  <MenuItem value="ACCEPTED">Aceptada</MenuItem>
                  <MenuItem value="REJECTED">Rechazada</MenuItem>
                  <MenuItem value="EXPIRED">Expirada</MenuItem>
                  <MenuItem value="CANCELLED">Cancelada</MenuItem>
                  <MenuItem value="COMPLETED">Completada</MenuItem>
                  <MenuItem value="INVOICED">Facturada</MenuItem>
                  <MenuItem value="PAID">Pagada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Filtrar por Email"
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Mes de Creación</InputLabel>
                <Select
                  value={filters.creationMonth}
                  label="Mes de Creación"
                  onChange={(e) => handleFilterChange('creationMonth', e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {getAvailableMonths().map(month => {
                    const [year, monthNum] = month.split('-');
                    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 
                                      'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                    const monthName = monthNames[parseInt(monthNum) - 1];
                    return (
                      <MenuItem key={month} value={month}>
                        {monthName} {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Alert 
          severity="info" 
          icon={<EditIcon fontSize="small" />}
          sx={{ mx: 3, mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.875rem' } }}
        >
          <strong>Tip:</strong> Haz doble click en cualquier fila para editar la cotización. Las cotizaciones en estado <strong>Aceptada</strong>, <strong>Cancelada</strong>, <strong>Rechazada</strong> o con <strong>Orden de Trabajo</strong> generada se abrirán en modo solo lectura.
        </Alert>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Folio</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Contacto</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Empresa</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Monto Total (con IVA)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha Creación</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha Aceptación</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Orden de Trabajo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Expira</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Notas</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQuotations.map((quotation) => (
                <TableRow 
                  key={quotation.id} 
                  hover
                  onDoubleClick={(e) => handleRowClick(quotation, e)}
                  title="Doble clic para abrir"
                  sx={{ 
                    '&:hover': { bgcolor: '#f9f9f9', cursor: 'pointer' },
                    cursor: 'pointer'
                  }}
                >
                  <TableCell>
                    {quotation.status === 'PENDING' ? (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: '#1976d2',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          '&:hover': { color: '#1565c0' }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/cotizaciones/new-quotation?edit=${quotation.id}`);
                        }}
                      >
                        {quotation.invoiceFolio || `COT-${String(quotation.id).padStart(6, '0')}`}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {quotation.invoiceFolio || `COT-${String(quotation.id).padStart(6, '0')}`}
                      </Typography>
                    )}
                    {quotation.billingInvoiceNumber && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        Factura: {quotation.billingInvoiceNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{quotation.name || 'N/A'}</TableCell>
                  <TableCell>{quotation.businessName || 'N/A'}</TableCell>
                  <TableCell>
                    {quotation.totalAmount ? (() => {
                      const { subtotal, iva, totalWithIva } = calculateIva(quotation.totalAmount);
                      return (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.95rem' }}>
                            {formatCurrency(totalWithIva)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {formatCurrency(subtotal)} + IVA {formatCurrency(iva)}
                          </Typography>
                        </>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
  {(() => {
    const isFinal = allowedTargets(quotation).length === 0;
    return (
      <FormControl size="small" fullWidth sx={{ minWidth: 140, background: isFinal ? '#f5f5f5' : '#f6f6fa', borderRadius: 2, border: '1px solid #e0e0e0', boxShadow: isFinal ? 0 : 1 }}>
        <Select
          value={quotation.status}
          onChange={e => {
            e.stopPropagation();
            handleStatusChange(quotation, e.target.value);
          }}
          onClick={e => e.stopPropagation()}
          displayEmpty
          disabled={isFinal}
          variant="outlined"
          sx={{
            fontWeight: 'bold',
            color: quotation.status === 'ACCEPTED' ? '#388e3c' : quotation.status === 'REJECTED' ? '#d32f2f' : quotation.status === 'DRAFT' ? '#5c6bc0' : '#1976d2',
            '& .MuiSelect-icon': { color: isFinal ? '#bdbdbd' : '#1976d2' }
          }}
          MenuProps={{ PaperProps: { sx: { borderRadius: 2 } } }}
        >
          {QUOTATION_STATUS_OPTIONS
            .filter(([value]) => value === quotation.status || allowedTargets(quotation).includes(value))
            .map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  })()}
</TableCell>
                  <TableCell>{formatDate(quotation.createdAt || quotation.created_at || quotation.dateCreated || quotation.creationDate)}</TableCell>
                  <TableCell>
                    {quotation.acceptedAt || quotation.accepted_at || quotation.dateAccepted || quotation.acceptanceDate ? (
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                        {formatDate(quotation.acceptedAt || quotation.accepted_at || quotation.dateAccepted || quotation.acceptanceDate)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {quotation.workOrderNumber ? (
                      <Chip 
                        label={quotation.workOrderNumber} 
                        size="small"
                        sx={{ 
                          bgcolor: '#e3f2fd', 
                          color: '#1976d2',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = '/servicios/agenda';
                        }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>
                    )}
                    {quotation.pendingCostReviews > 0 && (
                      <Tooltip title="Operaciones corrigió en sitio datos con los que se cotizó — revisa si impacta el costo">
                        <Chip
                          label={`💲 Revisar costo${quotation.pendingCostReviews > 1 ? ` (${quotation.pendingCostReviews})` : ''}`}
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setCostReviewQuotation(quotation); }}
                          sx={{ mt: 0.5, display: 'flex', width: 'fit-content', bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700, cursor: 'pointer', border: '1px solid #ffcc80' }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(quotation.expiresAt || quotation.expires_at || quotation.dateExpires || quotation.expirationDate)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {quotation.email || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={notesCount[quotation.id] > 0 ? `${notesCount[quotation.id]} nota(s) — clic para ver` : 'Sin notas — clic para agregar'}>
                        <IconButton
                          size="small"
                          onClick={e => { e.stopPropagation(); handleOpenNotes(e, quotation); }}
                          sx={{ color: notesCount[quotation.id] > 0 ? '#f57c00' : '#bdbdbd' }}
                        >
                          <StickyNote2Icon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {notesCount[quotation.id] > 0 && (
                        <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 700, lineHeight: 1 }}>
                          {notesCount[quotation.id]}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {/* Botón de acciones restaurado */}
                    {/* Menú de acciones */}
                    <Box>
                      <Tooltip title="Acciones">
                        <IconButton
                          size="small"
                          sx={{ color: '#1976d2' }}
                          onClick={e => {
                            e.stopPropagation(); // Evitar que active el clic de la fila
                            setActionMenuAnchorEl(e.currentTarget);
                            setActionMenuQuotation(quotation);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Menú contextual */}
                      {actionMenuQuotation && actionMenuQuotation.id === quotation.id && (
                        <Menu
                          anchorEl={actionMenuAnchorEl}
                          open={Boolean(actionMenuAnchorEl)}
                          onClose={() => { suppressRowClickRef.current = true; setTimeout(() => { suppressRowClickRef.current = false; }, 100); setActionMenuAnchorEl(null); setActionMenuQuotation(null); }}
                        >
                          <MenuItem
                            onClick={async () => {
                              setActionMenuAnchorEl(null);
                              setActionMenuQuotation(null);
                              await handleSendReminderDirect(quotation);
                            }}
                            disabled={!quotation.email}
                          >
                            <Email fontSize="small" sx={{ mr: 1 }} /> Reenviar correo al usuario
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setActionMenuAnchorEl(null);
                              setActionMenuQuotation(null);
                              setPreviewQuotationId(quotation.id);
                              setPreviewOpen(true);
                            }}
                          >
                            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> Visualizar Cotización
                          </MenuItem>
                          <MenuItem
                            onClick={async () => {
                              setActionMenuAnchorEl(null);
                              setActionMenuQuotation(null);
                              await handleSaveToDropbox(quotation);
                            }}
                          >
                            <CloudUpload fontSize="small" sx={{ mr: 1 }} /> Guardar en Dropbox
                          </MenuItem>
                          
                          {canRegisterInvoice(quotation) && (
                            <MenuItem
                              onClick={() => {
                                setActionMenuAnchorEl(null);
                                setActionMenuQuotation(null);
                                openInvoiceDialog(quotation);
                              }}
                            >
                              <ReceiptLongIcon fontSize="small" sx={{ mr: 1 }} /> Registrar factura
                            </MenuItem>
                          )}

                          {/* Opciones de versiones - solo para CANCELLED o REJECTED */}
                          {(quotation.status === 'CANCELLED' || quotation.status === 'REJECTED') && (
                            <MenuItem
                              onClick={() => {
                                setActionMenuAnchorEl(null);
                                setActionMenuQuotation(null);
                                handleOpenVersionDialog(quotation);
                              }}
                              sx={{ color: '#1976d2', fontWeight: 'bold' }}
                            >
                              <ContentCopy fontSize="small" sx={{ mr: 1 }} /> Reenviar Nueva Versión
                            </MenuItem>
                          )}
                          
                          {/* Ver historial de versiones - si tiene versión > 1 o tiene parent */}
                          {(quotation.version > 1 || quotation.parentQuotationId) && (
                            <MenuItem
                              onClick={() => {
                                setActionMenuAnchorEl(null);
                                setActionMenuQuotation(null);
                                handleShowVersionHistory(quotation);
                              }}
                            >
                              <History fontSize="small" sx={{ mr: 1 }} /> Ver Historial de Versiones
                            </MenuItem>
                          )}

                          {isAdmin && (
                            <MenuItem
                              onClick={() => {
                                setActionMenuAnchorEl(null);
                                setActionMenuQuotation(null);
                                setQuotationToDelete(quotation);
                                setDeleteConfirmOpen(true);
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Eliminar
                            </MenuItem>
                          )}

                        </Menu>
                      )}
                    </Box>
                                      </TableCell>
                </TableRow>
              ))}
              {filteredQuotations.length === 0 && quotations.length > 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                      No se encontraron cotizaciones con los filtros aplicados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {quotations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                      No hay cotizaciones disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página"
        />
      </Paper>

      {/* Diálogo de Edición de Cotización */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingQuotation(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3
        }}>
          <Assignment />
          {editingQuotation?.workOrderNumber ? 'Visualizar' : editingQuotation?.status === 'ACCEPTED' ? 'Visualizar' : 'Editar'} Cotización - {editingQuotation?.invoiceFolio || `COT-${String(editingQuotation?.id).padStart(6, '0')}`}
          {editingQuotation?.workOrderNumber ? (
            <Chip
              label={`OT: ${editingQuotation.workOrderNumber}`}
              size="small"
              sx={{ ml: 2, bgcolor: '#d32f2f', color: 'white', fontWeight: 'bold' }}
            />
          ) : editingQuotation?.status === 'ACCEPTED' && (
            <Chip 
              label="ACEPTADA" 
              size="small" 
              sx={{ 
                ml: 2, 
                bgcolor: '#4caf50', 
                color: 'white',
                fontWeight: 'bold'
              }} 
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 4, bgcolor: '#fafbfc' }}>
          {editingQuotation && (
            <Box>
              {/* Información General */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#24292e', fontWeight: 700, borderBottom: '2px solid #e1e4e8', pb: 2 }}>
                  📋 Información General
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Folio de Cotización"
                      value={editFormData.invoiceFolio}
                      helperText={editingQuotation?.billingInvoiceNumber ? `Factura: ${editingQuotation.billingInvoiceNumber}` : undefined}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Estado</InputLabel>
                      <Select
                        value={editFormData.status}
                        label="Estado"
                        onChange={(e) => handleEditFormChange('status', e.target.value)}
                        disabled={allowedTargets(editingQuotation).filter(s => s !== 'ACCEPTED').length === 0}
                        sx={{ 
                          borderRadius: 2,
                          bgcolor: (editingQuotation?.workOrderNumber || editingQuotation?.status === 'ACCEPTED') ? '#f5f5f5' : 'white'
                        }}
                      >
                        {QUOTATION_STATUS_OPTIONS
                          .filter(([value]) => value === editingQuotation?.status
                            || (value !== 'ACCEPTED' && allowedTargets(editingQuotation).includes(value)))
                          .map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Subtotal (del Servicio)"
                      value={`$${editFormData.subtotal?.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Requiere Factura</InputLabel>
                      <Select
                        value={editFormData.requiresInvoice}
                        label="Requiere Factura"
                        onChange={(e) => handleEditFormChange('requiresInvoice', e.target.value === 'true')}
                        disabled={!!editingQuotation?.workOrderNumber || ['ACCEPTED', 'CANCELLED'].includes(editingQuotation?.status)}
                        sx={{ 
                          borderRadius: 2,
                          bgcolor: (editingQuotation?.workOrderNumber || editingQuotation?.status === 'ACCEPTED') ? '#f5f5f5' : 'white'
                        }}
                      >
                        <MenuItem value={true}>Sí</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Comentarios"
                      value={editFormData.comments}
                      onChange={(e) => handleEditFormChange('comments', e.target.value)}
                      variant="outlined"
                      multiline
                      rows={4}
                      placeholder="Agregar comentarios o notas sobre la cotización..."
                      disabled={!!editingQuotation?.workOrderNumber || ['ACCEPTED', 'CANCELLED'].includes(editingQuotation?.status)}
                      InputProps={{ 
                        readOnly: !!editingQuotation?.workOrderNumber || editingQuotation?.status === 'ACCEPTED'
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: (editingQuotation?.workOrderNumber || editingQuotation?.status === 'ACCEPTED') ? '#f5f5f5' : 'white'
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Información de Solo Lectura */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#24292e', fontWeight: 700, borderBottom: '2px solid #e1e4e8', pb: 2 }}>
                  ℹ️ Información de Referencia (Solo Lectura)
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Cliente"
                      value={editingQuotation.name || 'N/A'}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={editingQuotation.email || 'N/A'}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Empresa"
                      value={editingQuotation.businessName || 'N/A'}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Servicio"
                      value={editingQuotation.serviceName || 'N/A'}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Fecha de Creación"
                      value={new Date(editingQuotation.createdAt || editingQuotation.created_at).toLocaleString('es-MX')}
                      variant="outlined"
                      InputProps={{ readOnly: true }}
                      sx={{ 
                        backgroundColor: '#f5f5f5',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#fafbfc', borderTop: '2px solid #e1e4e8', gap: 2 }}>
          <Button 
            onClick={() => {
              setEditDialogOpen(false);
              setEditingQuotation(null);
              setEditFormData({});
            }}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#d0d7de',
              color: '#24292e',
              '&:hover': {
                borderColor: '#667eea',
                bgcolor: '#f6f8fa'
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveQuotation}
            variant="contained" 
            disabled={loading || !!editingQuotation?.workOrderNumber || editingQuotation?.status === 'ACCEPTED'}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            sx={{ 
              borderRadius: 2,
              px: 5,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.6)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmación para Crear Nueva Versión */}
      <Dialog
        open={versionDialogOpen}
        onClose={() => {
          setVersionDialogOpen(false);
          setSelectedQuotationForVersion(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <ContentCopy />
          Crear Nueva Versión de Cotización
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {selectedQuotationForVersion && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Cotización actual:</strong>
              </Typography>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mb: 3 }}>
                <Typography variant="body2">• <strong>Folio:</strong> {selectedQuotationForVersion.invoiceFolio}</Typography>
                <Typography variant="body2">• <strong>Versión:</strong> {selectedQuotationForVersion.version || 1}</Typography>
                <Typography variant="body2">• <strong>Estado:</strong> {selectedQuotationForVersion.status}</Typography>
                <Typography variant="body2">• <strong>Cliente:</strong> {selectedQuotationForVersion.name}</Typography>
              </Box>
              
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Se creará una nueva versión con:</strong>
              </Typography>
              <Box sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 2 }}>
                <Typography variant="body2">✓ Todos los datos copiados</Typography>
                <Typography variant="body2">✓ Estado: BORRADOR (editable)</Typography>
                <Typography variant="body2">✓ Folio: {selectedQuotationForVersion.baseFolio || selectedQuotationForVersion.invoiceFolio}-V{(selectedQuotationForVersion.version || 1) + 1}</Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                ¿Desea continuar?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setVersionDialogOpen(false);
              setSelectedQuotationForVersion(null);
            }}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateNewVersion}
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              }
            }}
          >
            {loading ? 'Creando...' : 'Crear Versión'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Historial de Versiones */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          setVersionHistory([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <History />
          Historial de Versiones
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {loadingVersions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : versionHistory.length > 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Folio Base: <strong>{versionHistory[0]?.baseFolio || versionHistory[0]?.invoiceFolio}</strong>
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Versión</strong></TableCell>
                      <TableCell><strong>Folio</strong></TableCell>
                      <TableCell><strong>Estado</strong></TableCell>
                      <TableCell><strong>Fecha Creación</strong></TableCell>
                      <TableCell><strong>Última</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versionHistory.map((version) => (
                      <TableRow 
                        key={version.id}
                        sx={{ 
                          bgcolor: version.isLatestVersion ? '#e3f2fd' : 'transparent',
                          '&:hover': { bgcolor: version.isLatestVersion ? '#bbdefb' : '#f5f5f5' }
                        }}
                      >
                        <TableCell>
                          <Chip 
                            label={`V${version.version}`} 
                            size="small" 
                            color={version.isLatestVersion ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{version.invoiceFolio}</TableCell>
                        <TableCell>
                          <Chip 
                            label={version.status} 
                            size="small"
                            color={
                              version.status === 'ACCEPTED' ? 'success' :
                              version.status === 'REJECTED' ? 'error' :
                              version.status === 'CANCELLED' ? 'error' :
                              version.status === 'PENDING' ? 'warning' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {version.createdAt ? new Date(version.createdAt).toLocaleDateString('es-MX') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {version.isLatestVersion && <CheckCircle color="success" fontSize="small" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              No se encontraron versiones
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setHistoryDialogOpen(false);
              setVersionHistory([]);
            }}
            variant="contained"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!invoiceQuotation} onClose={() => !invoiceSaving && setInvoiceQuotation(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Registrar factura</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {invoiceQuotation?.invoiceFolio} — {invoiceQuotation?.businessName}
            {invoiceQuotation?.totalAmount ? ` — ${formatCurrency(invoiceQuotation.totalAmount)} + IVA` : ''}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Número de factura"
            value={invoiceNumber}
            onChange={(e) => { setInvoiceNumber(e.target.value); setInvoiceError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRegisterInvoice(); }}
            inputProps={{ maxLength: 50 }}
            error={!!invoiceError}
            helperText={invoiceError || 'La cotización pasará a Facturada. El folio COT no cambia.'}
            disabled={invoiceSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceQuotation(null)} disabled={invoiceSaving}>Cancelar</Button>
          <Button variant="contained" onClick={handleRegisterInvoice} disabled={invoiceSaving || !invoiceNumber.trim()}
            startIcon={invoiceSaving ? <CircularProgress size={16} /> : <ReceiptLongIcon />}>
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación permanente de cotización — solo admin */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 700 }}>
          Eliminar cotización permanentemente
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Vas a eliminar la cotización <strong>{quotationToDelete?.folio || quotationToDelete?.invoiceFolio}</strong> de{' '}
            <strong>{quotationToDelete?.businessName || quotationToDelete?.clientName}</strong> y TODA su información relacionada:
          </Typography>
          <Typography component="ul" sx={{ mb: 2, pl: 3 }}>
            <li>Orden de trabajo, programación por ente y evaluaciones de inspección</li>
            <li>UIPC, capacitaciones y constancias generadas</li>
            <li>Documentos, notas y pagos registrados</li>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Los archivos ya subidos a Dropbox NO se eliminan.
          </Typography>
          {(() => {
            const parent = quotationToDelete?.parentQuotationId
              ? quotations.find(q => q.id === quotationToDelete.parentQuotationId)
              : null;
            return parent && parent.status === 'CANCELLED' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Esta versión reemplaza a la <strong>{parent.invoiceFolio}</strong>, que se canceló: al cliente se le avisó que recibiría una nueva.
                Si la eliminas, tendrás que crear otra versión desde la {parent.invoiceFolio}.
              </Alert>
            ) : null;
          })()}
          <Typography sx={{ fontWeight: 700 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteConfirm} disabled={deletingQuotation}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteQuotation} variant="contained" color="error" disabled={deletingQuotation}>
            {deletingQuotation ? 'Eliminando...' : 'Eliminar definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Gestión de Sucursales */}
      <BranchManagementModal
        open={branchModalOpen}
        onClose={() => {
          setBranchModalOpen(false);
          setSelectedQuotationForBranches(null);
        }}
        quotation={selectedQuotationForBranches}
        onSuccess={(updatedQuotation) => {
          setSnackbar({
            open: true,
            message: `Sucursales actualizadas exitosamente. Nuevo monto: $${updatedQuotation.totalAmount?.toFixed(2)}`,
            severity: 'success'
          });
          fetchQuotations(); // Recargar tabla
        }}
      />

      {/* Popover de Notas de Cotización */}
      <Popover
        open={Boolean(notesAnchorEl)}
        anchorEl={notesAnchorEl}
        onClose={handleCloseNotes}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 360, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 2, bgcolor: '#f57c00', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <StickyNote2Icon />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Notas</Typography>
            {activeNotesQuotationId && (
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {quotations.find(q => q.id === activeNotesQuotationId)?.invoiceFolio ||
                  `COT-${String(activeNotesQuotationId).padStart(6, '0')}`}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={handleCloseNotes} sx={{ color: '#fff', ml: 'auto' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, maxHeight: 280, overflowY: 'auto' }}>
          {notesLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: '#f57c00' }} /></Box>
          ) : notesList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Sin notas aún
            </Typography>
          ) : (
            notesList.map((note, idx) => (
              <Box key={note.id || idx} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 700 }}>
                    {note.createdBy || 'Sistema'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {note.createdAt
                      ? new Date(note.createdAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
                      : ''}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ bgcolor: '#fff8e1', borderRadius: 1.5, p: 1, fontSize: '0.82rem' }}>
                  {note.content}
                </Typography>
                {idx < notesList.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Escribe una nota..."
            value={noteInput}
            onChange={e => setNoteInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSaveNote(); }}
            sx={{ mb: 1 }}
          />
          <Button
            fullWidth
            variant="contained"
            size="small"
            disabled={notesSaving || !noteInput.trim()}
            onClick={handleSaveNote}
            startIcon={notesSaving ? <CircularProgress size={14} color="inherit" /> : <NoteAddIcon />}
            sx={{
              bgcolor: '#f57c00',
              '&:hover': { bgcolor: '#e65100' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            {notesSaving ? 'Guardando...' : 'Agregar nota'}
          </Button>
        </Box>
      </Popover>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Preview de Cotización */}
      <CostReviewDialog
        open={!!costReviewQuotation}
        quotation={costReviewQuotation}
        canResolve={canResolveCostReviews}
        onClose={() => setCostReviewQuotation(null)}
        onResolved={() => fetchQuotations()}
      />

      <QuotationPreview
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewQuotationId(null);
        }}
        quotationId={previewQuotationId}
      />
    </Box>
  );
};

export default EstadisticasCotizaciones;
